import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    DropdownMenu,
    Label,
    Modal,
    ModalFeedback,
    SegmentedControl,
    Skeleton,
    SparkIcon,
    SparkModalPortal,
    TooltipTrigger,
    sparkToast,
    useOverlayEscape,
} from '@spark';
import { sampleRecordsFromUploadEntry } from '@/features/datasets/utils/filePreviewUtils';
import {
    formatOriginalColumnPreview,
    getFieldValue,
} from '@/features/datasets/utils/nestedFieldValue';
import FieldPreviewCard from '../FieldPreviewCard';
import {
    alignTemplateToText,
    applyValueToTemplate,
    buildMappedTemplate,
    collectExtractionFailures,
    ensureUniqueFieldName,
    fieldOccurrences,
    fieldsInTemplate,
    insertFieldAtOffset,
    isTextualValue,
    recordValuesForColumns,
    sanitizeFieldName,
    unmapField,
} from './promptMappingUtils';
import PromptTemplateEditor from './PromptTemplateEditor';
import AvailableFieldsPanel from './AvailableFieldsPanel';
import InsertFieldsMenu from './InsertFieldsMenu';
import NewFieldForm from './NewFieldForm';

/** How many records to sample for the mapping / shuffle preview. */
const SAMPLE_SIZE = 20;

/** The Save-time extraction check covers the first N examples (fewer when the
 * file has fewer). */
const VALIDATION_SAMPLE_SIZE = 10;

/** Debounce for the live Primary-input preview while typing in the editor. */
const PRIMARY_PREVIEW_DEBOUNCE_MS = 300;

/** Fields-menu popover height cap (its body max-height + new-field footer). */
const MENU_MAX_HEIGHT = 360;

/** Smallest useful popover: the new-field footer plus a couple of rows. */
const MENU_MIN_HEIGHT = 160;

const MODE_ITEMS = [
    { id: 'edit', label: 'Edit Template' },
    { id: 'original', label: 'Show original text' },
];

const PRIMARY_TOOLTIP =
    'The dynamic field whose extracted value is used as the model input for each record.';

/** Figma 581:59009 editor skeleton line widths (percent of the editor). `null` is a paragraph gap. */
const EDITOR_SKELETON_LINE_WIDTHS = [
    '57%',
    '84%',
    '52%',
    '30%',
    null,
    '78%',
    '83%',
    '9%',
    '81%',
    '16%',
];

function PromptEditorSkeleton() {
    return (
        <div
            className="cw-pm-editor cw-pm-editor-skeleton cw-pm-skeleton"
            data-testid="cw-pm-editor-skeleton"
            aria-hidden="true"
        >
            {EDITOR_SKELETON_LINE_WIDTHS.map((width, index) =>
                width == null ? (
                    <span key={index} className="cw-pm-editor-skeleton__break" />
                ) : (
                    <Skeleton key={index} shapeType="rectangle" width={width} height={15} />
                )
            )}
        </div>
    );
}

/** Same bar pattern as the editor skeleton, in the Primary-input card chrome. */
function PrimaryPreviewSkeleton() {
    return (
        <div
            className="cw-dc-example-preview cw-pm-primary-preview-skeleton cw-pm-skeleton"
            data-testid="cw-pm-primary-preview-skeleton"
            aria-busy="true"
            aria-hidden="true"
        >
            {EDITOR_SKELETON_LINE_WIDTHS.map((width, index) =>
                width == null ? (
                    <span key={index} className="cw-pm-editor-skeleton__break" />
                ) : (
                    <Skeleton key={index} shapeType="rectangle" width={width} height={15} />
                )
            )}
        </div>
    );
}

function exampleTextOf(record, exampleColumn) {
    if (!record) return '';
    const raw = getFieldValue(record, exampleColumn);
    if (raw === undefined || raw === null) return '';
    return formatOriginalColumnPreview(raw);
}

/**
 * "Map prompt template" modal (Figma Prompt-Mapper 483-27047): auto-maps
 * dataset-column values of a sampled example to `{{placeholders}}`, lets the
 * user define custom fields (text selection or `{{` / `/` menu), pick the
 * primary model input, and commits the draft to the caller only on Save.
 */
const MapPromptTemplateModal = ({
    /** Upload-slot entry (file bytes or library sample records). */
    slot,
    /** The selected example field whose value is being mapped. */
    exampleColumn,
    /** All file columns (the example field itself is excluded internally). */
    columns,
    /** Saved state when re-opening: { template, customFields, primaryField }. */
    initial,
    onSave,
    onCancel,
}) => {
    const otherColumns = useMemo(
        () => (columns || []).filter((column) => column !== exampleColumn),
        [columns, exampleColumn]
    );

    const sampleExampleRecords = useCallback(
        (size) =>
            sampleRecordsFromUploadEntry(slot, size).filter((record) =>
                exampleTextOf(record, exampleColumn).trim()
            ),
        [slot, exampleColumn]
    );

    const [records, setRecords] = useState([]);
    const [contentLoading, setContentLoading] = useState(true);
    const [shuffling, setShuffling] = useState(false);
    const loading = contentLoading || shuffling;

    const [recordIndex, setRecordIndex] = useState(0);
    const record = records[recordIndex] ?? null;
    const originalText = exampleTextOf(record, exampleColumn);
    const recordValues = useMemo(
        () => recordValuesForColumns(record, otherColumns),
        [record, otherColumns]
    );

    const [customFields, setCustomFields] = useState(() => ({ ...(initial?.customFields || {}) }));
    const [template, setTemplate] = useState(() => initial?.template || '');
    const initialCustomFieldsRef = useRef(initial?.customFields);
    const didAutoMapRef = useRef(Boolean(initial?.template));
    const shuffleRafRef = useRef([]);

    // Defer sampling so the first paint can show Figma 581:59009 skeletons.
    useEffect(() => {
        let cancelled = false;
        setContentLoading(true);
        const timer = setTimeout(() => {
            const sampled = sampleExampleRecords(SAMPLE_SIZE);
            if (cancelled) return;
            setRecords(sampled);
            setRecordIndex(0);
            if (!didAutoMapRef.current) {
                const first = sampled[0] ?? null;
                const text = exampleTextOf(first, exampleColumn);
                const values = recordValuesForColumns(first, otherColumns);
                setTemplate(
                    buildMappedTemplate(text, values, initialCustomFieldsRef.current || {})
                        .template
                );
                didAutoMapRef.current = true;
            }
            setContentLoading(false);
        }, 0);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [sampleExampleRecords, exampleColumn, otherColumns]);

    useEffect(
        () => () => {
            shuffleRafRef.current.forEach((id) => cancelAnimationFrame(id));
        },
        []
    );

    const [primaryField, setPrimaryField] = useState(initial?.primaryField || null);
    // True once the user explicitly picked a primary (or one was saved): the
    // first added custom field takes over an auto-defaulted primary, but
    // never an explicit choice.
    const userPickedPrimaryRef = useRef(Boolean(initial?.primaryField));
    const [mode, setMode] = useState('edit');

    // Bumped on every non-typing template mutation; remounts the edit surface.
    const [renderVersion, setRenderVersion] = useState(0);
    const bump = useCallback(() => setRenderVersion((v) => v + 1), []);

    const caretOffsetRef = useRef(null);
    const editorWrapRef = useRef(null);
    const insertMenuRef = useRef(null);
    const selectionMenuRef = useRef(null);
    const [selectionInfo, setSelectionInfo] = useState(null);
    const [triggerInfo, setTriggerInfo] = useState(null);
    // Keyboard highlight over the flattened menu items (columns then custom
    // fields — must match InsertFieldsMenu's render order).
    const [menuActiveIndex, setMenuActiveIndex] = useState(0);

    const mappedFields = useMemo(() => fieldsInTemplate(template), [template]);
    const occurrences = useMemo(() => fieldOccurrences(template), [template]);

    // What each custom placeholder stands for in the current example, for the
    // panel's value line (matching the dataset-column cards). Stored values
    // lag reality — menu-created fields hold '' and selection-created ones
    // keep another record's text until a shuffle realigns them — so read the
    // current alignment first and fall back to the stored text.
    const customFieldPreviews = useMemo(() => {
        const previews = { ...customFields };
        const aligned = alignTemplateToText(template, originalText);
        (aligned || []).forEach((segment) => {
            if (segment.type === 'field' && segment.text && segment.name in previews) {
                previews[segment.name] = segment.text;
            }
        });
        return previews;
    }, [customFields, template, originalText]);

    const menuFieldNames = useMemo(
        () => [...otherColumns, ...Object.keys(customFields)],
        [otherColumns, customFields]
    );

    const columnsMeta = useMemo(
        () =>
            otherColumns.map((name) => ({
                name,
                value: recordValues[name] || '',
                textual: isTextualValue(getFieldValue(record, name)),
            })),
        [otherColumns, recordValues, record]
    );

    /** Save-blocking message shown in red above the footer (with a toast). */
    const [saveError, setSaveError] = useState(null);
    // The primary whose placeholder was removed from the template. The select
    // is emptied instead of erroring live; a Save attempt then surfaces the
    // red alert naming this field. Mapping it back restores the choice.
    const [removedPrimary, setRemovedPrimary] = useState(null);

    // Default primary = first mapped field. A primary whose placeholder was
    // later removed from the template empties the select — it is never
    // silently reassigned while the removed field is pending.
    useEffect(() => {
        if (primaryField && !mappedFields.includes(primaryField)) {
            setRemovedPrimary(primaryField);
            setPrimaryField(null);
            bump();
            return;
        }
        if (!primaryField && removedPrimary && mappedFields.includes(removedPrimary)) {
            setPrimaryField(removedPrimary);
            setRemovedPrimary(null);
            bump();
            return;
        }
        if (!primaryField && !removedPrimary && mappedFields.length > 0) {
            setPrimaryField(mappedFields[0]);
            bump();
        }
    }, [mappedFields, primaryField, removedPrimary, bump]);

    /** The chosen primary field no longer appears in the template. */
    const primaryMissing = Boolean(primaryField) && !mappedFields.includes(primaryField);

    // Live "Primary input" preview: what the chosen primary extracts from the
    // current example with the current template (same endpoint and semantics
    // as the upload). Debounced so typing in the editor doesn't hammer the
    // backend. Shuffle / first-load keep the card mounted and swap in
    // skeletons until the next extraction lands.
    const [primaryPreview, setPrimaryPreview] = useState(null);
    const [primaryPreviewLoading, setPrimaryPreviewLoading] = useState(false);
    const primaryPreviewRef = useRef(primaryPreview);
    primaryPreviewRef.current = primaryPreview;
    useEffect(() => {
        if (!primaryField || primaryMissing) {
            setPrimaryPreview(null);
            setPrimaryPreviewLoading(false);
            return undefined;
        }
        if (!originalText.trim()) {
            if (loading) {
                setPrimaryPreviewLoading(true);
            } else {
                setPrimaryPreview(null);
                setPrimaryPreviewLoading(false);
            }
            return undefined;
        }
        if (loading) {
            setPrimaryPreviewLoading(true);
            return undefined;
        }
        let cancelled = false;
        if (!primaryPreviewRef.current) {
            setPrimaryPreviewLoading(true);
        }
        const timer = setTimeout(() => {
            fetch('/api/preview-field-extraction', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: originalText,
                    prompt_template: template,
                    dynamic_fields: fieldsInTemplate(template),
                    example_field_name: primaryField,
                }),
            })
                .then((res) => res.json())
                .then((result) => {
                    if (cancelled) return;
                    setPrimaryPreview((result?.success && result.extracted_example) || null);
                    setPrimaryPreviewLoading(false);
                })
                .catch(() => {
                    if (cancelled) return;
                    setPrimaryPreview(null);
                    setPrimaryPreviewLoading(false);
                });
        }, PRIMARY_PREVIEW_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [loading, primaryField, primaryMissing, template, originalText]);
    const showPrimaryPreviewSkeleton =
        Boolean(primaryField) && !primaryMissing && (loading || primaryPreviewLoading);
    // The extraction can legitimately come back empty (a custom-field primary
    // whose stored value doesn't align with this example, a placeholder at
    // the template's edge, a failed request). Fall back to the field's known
    // value so the Primary input card never vanishes while a primary is
    // mapped — Save still runs the real batch extraction check.
    const primaryPreviewText =
        primaryPreview ||
        (primaryField && (recordValues[primaryField] || customFields[primaryField])) ||
        '';

    /**
     * Viewport rect → position inside the editor wrapper.
     *
     * The popover must land fully INSIDE the wrap: anything hanging below it
     * extends the modal body's scroll area, and the menu's own
     * scroll-into-view then strands the whole modal in blank space. So the
     * anchor is clamped into the wrap (selection lines can sit outside the
     * visible box when the editor is scrolled), the menu opens on whichever
     * side has more room when the space below is tight, and its height is
     * capped to that room (the menu body scrolls internally).
     */
    const toLocalPosition = useCallback((rect) => {
        const wrap = editorWrapRef.current?.getBoundingClientRect();
        if (!rect || !wrap) return { top: 0, left: 0, above: false, maxHeight: MENU_MAX_HEIGHT };
        const clampY = (y) => Math.min(Math.max(y, 0), wrap.height);
        const anchorTop = clampY(rect.top - wrap.top);
        const anchorBottom = clampY(rect.bottom - wrap.top);
        const spaceBelow = wrap.height - anchorBottom - 6;
        const spaceAbove = anchorTop - 6;
        const above = spaceBelow < MENU_MIN_HEIGHT && spaceAbove > spaceBelow;
        const space = above ? spaceAbove : spaceBelow;
        const maxHeight = Math.min(Math.max(space, MENU_MIN_HEIGHT), MENU_MAX_HEIGHT);
        const left = Math.min(Math.max(rect.left - wrap.left, 0), Math.max(wrap.width - 300, 0));
        const top = above
            ? // Flipped (translateY(-100%)): keep the menu's top edge >= 0.
              Math.max(anchorTop - 6, maxHeight)
            : Math.min(anchorBottom + 6, wrap.height - maxHeight);
        return { top: Math.max(top, 0), left, above, maxHeight };
    }, []);

    const closeOverlays = useCallback(() => {
        setSelectionInfo(null);
        setTriggerInfo(null);
    }, []);

    const menuOpen = Boolean(triggerInfo || selectionInfo);

    // The open menu joins the shared overlay stack as the topmost overlay, so
    // Escape closes only the menu and the modal's own handler is skipped.
    useOverlayEscape(menuOpen, closeOverlays);

    // Highlight the first item whenever a menu (re)opens.
    useEffect(() => {
        setMenuActiveIndex(0);
    }, [triggerInfo, selectionInfo]);

    // Click-away closes whichever menu is open.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onMouseDown = (event) => {
            if (
                !insertMenuRef.current?.contains(event.target) &&
                !selectionMenuRef.current?.contains(event.target)
            ) {
                closeOverlays();
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [menuOpen, closeOverlays]);

    const handleModeChange = (id) => {
        setMode(id);
        closeOverlays();
        bump();
    };

    // Shuffle keeps the editor and fields panel mounted: static text and card
    // names stay put while the field values show inline skeletons. The double
    // rAF lets that loading state paint before the synchronous re-map work.
    const handleShuffle = () => {
        if (loading || records.length < 2) return;
        setShuffling(true);
        closeOverlays();
        const rafIds = [];
        rafIds.push(
            requestAnimationFrame(() => {
                rafIds.push(
                    requestAnimationFrame(() => {
                        let next = Math.floor(Math.random() * records.length);
                        if (next === recordIndex) next = (recordIndex + 1) % records.length;
                        const newRecord = records[next];
                        const newText = exampleTextOf(newRecord, exampleColumn);
                        const newValues = recordValuesForColumns(newRecord, otherColumns);
                        setRecordIndex(next);

                        // When the template's literal anchors fit the new record, the
                        // template applies as-is: keep every placeholder (notably custom
                        // fields, whose stored text is from the previous record or empty)
                        // and refresh each custom field's stored value to what its
                        // placeholder captures from the new record.
                        const aligned = alignTemplateToText(template, newText);
                        if (aligned) {
                            const captured = {};
                            aligned.forEach((segment) => {
                                if (
                                    segment.type === 'field' &&
                                    segment.text &&
                                    !(segment.name in captured)
                                ) {
                                    captured[segment.name] = segment.text;
                                }
                            });
                            setCustomFields((prev) => {
                                const nextFields = { ...prev };
                                Object.keys(prev).forEach((name) => {
                                    if (captured[name]) nextFields[name] = captured[name];
                                });
                                return nextFields;
                            });
                        } else {
                            // Old-record literals don't fit the new record (e.g. untouched
                            // auto-mapped template): fresh auto-map + re-applied custom-field
                            // texts — everything that still matches is kept.
                            setTemplate(
                                buildMappedTemplate(newText, newValues, customFields).template
                            );
                        }
                        caretOffsetRef.current = null;
                        bump();
                        setShuffling(false);
                    })
                );
                shuffleRafRef.current = rafIds;
            })
        );
        shuffleRafRef.current = rafIds;
    };

    const handleCaretChange = useCallback((offset) => {
        caretOffsetRef.current = offset;
    }, []);

    const handleSelectText = useCallback(
        (info) => {
            setSelectionInfo(info ? { ...info, position: toLocalPosition(info.rect) } : null);
        },
        [toLocalPosition]
    );

    const handleInsertTrigger = useCallback(
        (info) => {
            setTriggerInfo(info ? { ...info, position: toLocalPosition(info.rect) } : null);
        },
        [toLocalPosition]
    );

    const takenNames = useCallback(
        () => [...otherColumns, ...Object.keys(customFields)],
        [otherColumns, customFields]
    );

    /** The first custom field becomes the primary unless the user picked one. */
    const defaultPrimaryToFirstCustomField = (name) => {
        if (!userPickedPrimaryRef.current && Object.keys(customFields).length === 0) {
            setPrimaryField(name);
        }
    };

    /** Custom field from the text-selection popover: map every occurrence. */
    const handleAddFieldFromSelection = (rawName) => {
        const base = sanitizeFieldName(rawName);
        if (!base || !selectionInfo) return;
        const name = ensureUniqueFieldName(base, takenNames());
        const text = selectionInfo.text;
        setCustomFields((prev) => ({ ...prev, [name]: text }));
        setTemplate((prev) => applyValueToTemplate(prev, name, text));
        defaultPrimaryToFirstCustomField(name);
        closeOverlays();
        bump();
    };

    /** Replace the typed `{{` / `/` trigger with the picked placeholder. */
    const insertFromTrigger = (name) => {
        if (!triggerInfo) return;
        const { offset, triggerLength } = triggerInfo;
        setTemplate(
            (prev) =>
                `${prev.substring(0, offset - triggerLength)}{{${name}}}${prev.substring(offset)}`
        );
        closeOverlays();
        bump();
    };

    /** Replace the mouse-highlighted range with the picked field's placeholder. */
    const replaceSelectionWithField = (name) => {
        if (!selectionInfo) return;
        const { start, end } = selectionInfo;
        setTemplate((prev) => `${prev.substring(0, start)}{{${name}}}${prev.substring(end)}`);
        closeOverlays();
        bump();
    };

    const handleMenuNewField = (rawName) => {
        const base = sanitizeFieldName(rawName);
        if (!base || !triggerInfo) return;
        const name = ensureUniqueFieldName(base, takenNames());
        setCustomFields((prev) => ({ ...prev, [name]: '' }));
        defaultPrimaryToFirstCustomField(name);
        insertFromTrigger(name);
    };

    // ArrowUp/Down + Enter navigation while a menu is open. Capture phase so
    // the contentEditable never sees the keys; skipped while the new-field
    // input has focus so it keeps its own typing/Enter behaviour.
    const pickMenuItemRef = useRef(null);
    pickMenuItemRef.current = (name) => {
        if (triggerInfo) insertFromTrigger(name);
        else if (selectionInfo) replaceSelectionWithField(name);
    };
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.target instanceof Element && event.target.closest('input, textarea')) {
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                if (menuFieldNames.length === 0) return;
                event.preventDefault();
                event.stopPropagation();
                const delta = event.key === 'ArrowDown' ? 1 : -1;
                setMenuActiveIndex(
                    (prev) => (prev + delta + menuFieldNames.length) % menuFieldNames.length
                );
            } else if (event.key === 'Enter') {
                const name = menuFieldNames[menuActiveIndex];
                if (!name) return;
                event.preventDefault();
                event.stopPropagation();
                pickMenuItemRef.current?.(name);
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [menuOpen, menuFieldNames, menuActiveIndex]);

    /**
     * Map a field into the template: replace its value's occurrences when the
     * text contains them, otherwise insert `{{name}}` at the caret.
     */
    const mapFieldIn = (name, value) => {
        setTemplate((prev) => {
            const mapped = applyValueToTemplate(prev, name, value || '');
            if (mapped !== prev) return mapped;
            return insertFieldAtOffset(prev, name, caretOffsetRef.current ?? prev.length);
        });
        closeOverlays();
        bump();
    };

    const unmapFieldFrom = (name, value) => {
        setTemplate((prev) => unmapField(prev, name, value || ''));
        closeOverlays();
        bump();
    };

    const handleToggleColumn = (name, isMapped) => {
        if (isMapped) {
            unmapFieldFrom(name, recordValues[name]);
            return;
        }
        // A dataset-column placeholder only extracts a value when the column's
        // value actually appears in the example text — one that can't match
        // would just fail the extraction check on Save. Refuse to add it and
        // say why instead.
        const value = recordValues[name];
        if (!value) {
            sparkToast.error(
                `Can't add "${name}" to the template - it has no value in the current example.`
            );
            return;
        }
        if (!originalText.includes(value)) {
            sparkToast.error(
                `Can't add "${name}" to the template - its value doesn't appear in the example text.`
            );
            return;
        }
        mapFieldIn(name, value);
    };

    const handleToggleCustomField = (name, isMapped) => {
        if (isMapped) unmapFieldFrom(name, customFields[name]);
        else mapFieldIn(name, customFields[name]);
    };

    /** Custom field pending delete confirmation (trash on its card). */
    const [deleteCandidate, setDeleteCandidate] = useState(null);

    /**
     * Confirmed delete of a custom field: put the text its placeholders stand
     * for in the current example back in place (the aligned capture, falling
     * back to the stored selection text — same source as the card's value
     * line) and forget the field. Menu-created fields that never replaced any
     * text restore '', which just removes the placeholder.
     */
    const handleDeleteCustomField = (name) => {
        setTemplate((prev) => unmapField(prev, name, customFieldPreviews[name] || ''));
        setCustomFields((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
        // A deleted primary falls back to the first mapped field (default effect).
        if (primaryField === name) setPrimaryField(null);
        closeOverlays();
        bump();
    };

    /** Only mapped fields are offered, so picking is just selecting. */
    const handlePickPrimary = (name) => {
        userPickedPrimaryRef.current = true;
        setRemovedPrimary(null);
        bump();
        setPrimaryField(name);
    };

    // Only fields currently mapped in the template (checked in the
    // Available-fields panel) qualify as the primary model input.
    const mappedCustomFieldNames = Object.keys(customFields).filter((name) =>
        mappedFields.includes(name)
    );
    const mappedColumnNames = otherColumns.filter((name) => mappedFields.includes(name));
    const primaryPin = (name) =>
        name === primaryField ? (
            <SparkIcon slug="pin-filled" size="small" className="cw-pm-primary-select__pin" />
        ) : undefined;
    const toPrimaryItem = (name) => ({
        label: name,
        suffix: primaryPin(name),
        onClick: () => handlePickPrimary(name),
    });
    // Custom fields first (Figma 483:42147). Pin marks the current primary;
    // Spark's selected-state check is omitted so the pin is the only indicator.
    const primarySections = [
        ...(mappedCustomFieldNames.length
            ? [
                  {
                      title: 'Custom fields',
                      items: mappedCustomFieldNames.map(toPrimaryItem),
                  },
              ]
            : []),
        ...(mappedColumnNames.length
            ? [
                  {
                      title: 'Dataset columns',
                      items: mappedColumnNames.map(toPrimaryItem),
                  },
              ]
            : []),
    ];

    const [saving, setSaving] = useState(false);
    /** True when the checked examples missed fields → the warning replaces the mapper. */
    const [saveWarning, setSaveWarning] = useState(false);

    const blockSave = (message) => {
        setSaveError(message);
        sparkToast.error(message);
    };

    // A blocking message describes one Save attempt — editing the mapping or
    // switching the primary invalidates it.
    useEffect(() => {
        setSaveError(null);
    }, [template, primaryField]);

    const commitSave = useCallback(() => {
        onSave({ template, customFields, primaryField });
    }, [onSave, template, customFields, primaryField]);

    // Save stays clickable while the primary was removed from the template so
    // the attempt can surface the error (alert + toast); it is disabled only
    // when nothing was ever selected (or while the extraction check runs).
    // Before committing, the first 10 examples are batch-checked: an empty
    // primary on the current example hard blocks, any other failed extraction
    // opens the warning modal.
    const handleSave = async () => {
        if (loading) return;
        if (!primaryField || primaryMissing) {
            blockSave(
                `Primary model input "${primaryField || removedPrimary}" is not in the ` +
                    'template - map it before saving.'
            );
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            // Guarantee the currently shown example is part of the batch so
            // its result can drive the hard block below.
            const sampleTexts = sampleExampleRecords(VALIDATION_SAMPLE_SIZE).map((record) =>
                exampleTextOf(record, exampleColumn)
            );
            let currentIndex = sampleTexts.indexOf(originalText);
            let contents = sampleTexts;
            if (currentIndex === -1) {
                contents = [originalText, ...sampleTexts].slice(0, VALIDATION_SAMPLE_SIZE);
                currentIndex = 0;
            }

            const response = await fetch('/api/preview-field-extraction-batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents,
                    prompt_template: template,
                    dynamic_fields: mappedFields,
                    example_field_name: primaryField,
                }),
            });
            const result = await response.json();
            if (!result?.success) {
                throw new Error(result?.error || 'Extraction check failed');
            }
            const results = result.results || [];

            const current = results[currentIndex];
            if (!current?.extracted_example?.trim()) {
                blockSave(
                    `Primary model input "${primaryField}" extracts an empty value from the ` +
                        'example - adjust its position in the template.'
                );
                return;
            }

            const failures = collectExtractionFailures(results, mappedFields, primaryField);
            if (failures.failedRecords > 0) {
                setSaveWarning(true);
                return;
            }

            commitSave();
        } catch {
            blockSave('Could not verify field extraction - please try saving again.');
        } finally {
            setSaving(false);
        }
    };

    /** How many template uses the pending delete would remove. */
    const deleteCandidateUses = deleteCandidate ? occurrences[deleteCandidate] || 0 : 0;

    return (
        <SparkModalPortal open onClose={onCancel} size="wide" destroyOnClose>
            <Modal
                title="Map prompt template"
                showSubtitle={false}
                fixedHeight={false}
                primaryLabel={saving ? 'Validating…' : 'Save'}
                tertiaryLabel="Cancel"
                primaryDisabled={(!primaryField && !removedPrimary) || saving || loading}
                className="cw-pm-modal"
                onPrimaryClick={handleSave}
                onTertiaryClick={onCancel}
                onCloseClick={onCancel}
            >
                <div className="cw-pm-body" aria-busy={loading || undefined}>
                    <div className="cw-pm-main">
                        <div className="cw-pm-toolbar">
                            <SegmentedControl
                                size="small"
                                items={MODE_ITEMS}
                                activeId={mode}
                                onChange={handleModeChange}
                            />
                            <Button
                                size="small"
                                variant="secondary"
                                startIcon={<SparkIcon slug="arrows-shuffle" size="small" />}
                                disabled={loading || records.length < 2}
                                onClick={handleShuffle}
                            >
                                Shuffle
                            </Button>
                        </div>
                        <div className="cw-pm-editor-wrap" ref={editorWrapRef}>
                            {contentLoading ? (
                                <PromptEditorSkeleton />
                            ) : (
                                <PromptTemplateEditor
                                    mode={mode}
                                    template={template}
                                    originalText={originalText}
                                    recordValues={recordValues}
                                    customFields={customFields}
                                    primaryField={primaryField}
                                    renderVersion={renderVersion}
                                    shuffling={shuffling}
                                    onTemplateChange={setTemplate}
                                    onCaretChange={handleCaretChange}
                                    onSelectText={mode === 'edit' ? handleSelectText : undefined}
                                    onInsertTrigger={handleInsertTrigger}
                                    onRemoveField={(name) => handleToggleCustomField(name, true)}
                                />
                            )}
                            {selectionInfo && (
                                <div
                                    ref={selectionMenuRef}
                                    className={`cw-pm-popover cw-pm-popover--menu${selectionInfo.position.above ? ' cw-pm-popover--above' : ''}`}
                                    style={{
                                        top: selectionInfo.position.top,
                                        left: selectionInfo.position.left,
                                        maxHeight: selectionInfo.position.maxHeight,
                                    }}
                                >
                                    <InsertFieldsMenu
                                        columns={columnsMeta}
                                        customFields={customFields}
                                        activeIndex={menuActiveIndex}
                                        onPickColumn={replaceSelectionWithField}
                                        onPickCustomField={replaceSelectionWithField}
                                        footer={
                                            <NewFieldForm
                                                autoFocus={false}
                                                onAdd={handleAddFieldFromSelection}
                                            />
                                        }
                                    />
                                </div>
                            )}
                            {triggerInfo && (
                                <div
                                    ref={insertMenuRef}
                                    className={`cw-pm-popover cw-pm-popover--menu${triggerInfo.position.above ? ' cw-pm-popover--above' : ''}`}
                                    style={{
                                        top: triggerInfo.position.top,
                                        left: triggerInfo.position.left,
                                        maxHeight: triggerInfo.position.maxHeight,
                                    }}
                                >
                                    <InsertFieldsMenu
                                        columns={columnsMeta}
                                        customFields={customFields}
                                        activeIndex={menuActiveIndex}
                                        onPickColumn={insertFromTrigger}
                                        onPickCustomField={insertFromTrigger}
                                        footer={
                                            <NewFieldForm
                                                autoFocus={false}
                                                onAdd={handleMenuNewField}
                                            />
                                        }
                                    />
                                </div>
                            )}
                        </div>
                        <div className="cw-pm-primary-row">
                            <div className="cw-pm-primary-row__label">
                                <Label label="Primary model input" showAsterisk showIcon={false} />
                                <TooltipTrigger text={PRIMARY_TOOLTIP}>
                                    <span className="cw-help-icon">
                                        <SparkIcon slug="circle-info" size="small" />
                                    </span>
                                </TooltipTrigger>
                            </div>
                            <DropdownMenu
                                selectType="single-select"
                                size="medium"
                                searchable
                                searchMode="trigger"
                                showLabel={false}
                                placeholder="Select a mapped variable"
                                searchEmptyText="No matching fields"
                                text={primaryField || undefined}
                                sections={primarySections}
                                className="cw-pm-primary-select"
                                panelClassName="cw-pm-primary-select-panel"
                            />
                        </div>
                        {/* The slot always renders — CSS reserves the card's footprint —
                            so the Primary model input row above stays put whether a
                            primary is selected (card / skeleton) or not (empty). */}
                        <div className="cw-pm-primary-preview">
                            {showPrimaryPreviewSkeleton ? (
                                <PrimaryPreviewSkeleton />
                            ) : primaryField && !primaryMissing ? (
                                <FieldPreviewCard
                                    label="Primary input"
                                    text={
                                        primaryPreviewText ||
                                        'No value extracted for this example yet.'
                                    }
                                    columnName={primaryField}
                                    showShuffle={false}
                                    showPreview={Boolean(primaryPreviewText)}
                                />
                            ) : null}
                        </div>
                    </div>
                    <AvailableFieldsPanel
                        columns={columnsMeta}
                        customFields={customFields}
                        customFieldPreviews={customFieldPreviews}
                        occurrences={occurrences}
                        mappedFields={mappedFields}
                        primaryField={primaryField}
                        loading={contentLoading}
                        valuesLoading={shuffling}
                        interactive={mode === 'edit'}
                        onToggleColumn={handleToggleColumn}
                        onToggleCustomField={handleToggleCustomField}
                        onDeleteCustomField={setDeleteCandidate}
                        onSetPrimary={handlePickPrimary}
                    />
                </div>
                {saveError && (
                    <p className="cw-pm-save-error" role="alert">
                        {saveError}
                    </p>
                )}
            </Modal>
            {saveWarning ? (
                // Save-check warning stacked on top of the mapper (Figma
                // Prompt-Mapper 826:21188); the mapper stays visible behind it
                // so "Review fields" returns exactly where you were.
                <SparkModalPortal open onClose={() => setSaveWarning(false)} size="text">
                    <ModalFeedback
                        feedbackType="warning"
                        title="Some dynamic fields are missing"
                        content={
                            'The first examples we checked don’t fully match this template. ' +
                            'Review the examples and map any remaining fields.'
                        }
                        showClose
                        showOverlay={false}
                        primaryLabel="Review fields"
                        tertiaryLabel="Save anyway"
                        tertiaryVariant="secondary"
                        className="cw-pm-warning-modal"
                        onCloseClick={() => setSaveWarning(false)}
                        onPrimaryClick={() => setSaveWarning(false)}
                        onTertiaryClick={commitSave}
                    />
                </SparkModalPortal>
            ) : null}
            {deleteCandidate ? (
                // Confirmation before a custom field is deleted for good; the
                // mapper stays mounted (and visible) behind it.
                <SparkModalPortal open onClose={() => setDeleteCandidate(null)} size="text">
                    <Modal
                        title="Delete custom field?"
                        showSubtitle={false}
                        showClose
                        showScrollBar={false}
                        showOverlay={false}
                        fixedHeight={false}
                        bodyVariant="text"
                        danger
                        primaryLabel="Delete field"
                        tertiaryLabel="Cancel"
                        className="cw-pm-delete-field-modal"
                        onPrimaryClick={() => {
                            handleDeleteCustomField(deleteCandidate);
                            setDeleteCandidate(null);
                        }}
                        onTertiaryClick={() => setDeleteCandidate(null)}
                        onCloseClick={() => setDeleteCandidate(null)}
                    >
                        {deleteCandidateUses > 0
                            ? `This will remove ${deleteCandidateUses} use` +
                              `${deleteCandidateUses === 1 ? '' : 's'} of ${deleteCandidate} ` +
                              'from the template.'
                            : `${deleteCandidate} is not used in the template.`}
                    </Modal>
                </SparkModalPortal>
            ) : null}
        </SparkModalPortal>
    );
};

export default MapPromptTemplateModal;
